import { reportException } from '_lib/utils/sentryService';

import { CallCenterStorage } from '../../localStorage/callCenterStorage';

import {
  CreateResource,
  DeleteResource,
  GetByIdResource,
  ListOptions,
  ListResource,
  PutHook,
  PutResource,
  ReactivateResource,
  ResourceId,
  UnpagedListResource,
  UseCustomMutationOptions,
  createApiResource,
  useCustomMutation
} from '../apiResourceFactory';
import { API } from '../apiService';
import {
  Company,
  CompanyCreateApiDTO,
  CompanyListNameDTO,
  CompanyQueryParams,
  CompanyUpdateApiDTO,
  CompanyZipCodesUpdateDTO,
  ListResultWithoutPaging
} from '../apiTypes.shim';
import { ourQueryClient } from '../QueryProvider';

interface CompanyResource
  extends GetByIdResource<Company>,
    ListResource<Company, CompanyQueryParams>,
    UnpagedListResource<
      CompanyListNameDTO,
      Omit<CompanyQueryParams, 'serializer'> & { serializer: 'name' },
      { requireParams: true }
    >,
    CreateResource<CompanyCreateApiDTO, Company>,
    PutResource<CompanyUpdateApiDTO, Company>,
    DeleteResource,
    ReactivateResource<Company> {
  getCompanyByDnis: (companyDNIS: string) => Promise<Company | null>;
  unpagedListFull: (
    options: ListOptions<Omit<CompanyQueryParams, 'serializer'> & { serializer: 'full' }, { requireParams: true }>
  ) => Promise<ListResultWithoutPaging<Company>>;
  useUnpagedListFull: UnpagedListResource<
    Company,
    Omit<CompanyQueryParams, 'serializer'> & { serializer: 'full' },
    { requireParams: true }
  >['useUnpagedList'];
  useUpdateServicedZipCodes: PutHook<CompanyZipCodesUpdateDTO, Company>;
}

export const companyResourceUri = `company`;
export const createCompanyResource = (): CompanyResource => {
  const {
    useList,
    list,
    useGetById,
    getById,
    unpagedList,
    useUnpagedList,
    unpagedListQueryKey,
    listQueryKey,
    getByIdQueryKey,
    create,
    useCreate,
    put,
    usePut,
    deleteItem,
    useDelete,
    reactivate,
    useReactivate
  } = createApiResource(API, companyResourceUri);

  type UpdateServicedZipCodesVariables = { id: ResourceId; data: CompanyZipCodesUpdateDTO };
  const useUpdateServicedZipCodes = (
    mutationConfig?: UseCustomMutationOptions<Company, unknown, UpdateServicedZipCodesVariables>
  ) => {
    const context = useCustomMutation<Company, unknown, UpdateServicedZipCodesVariables>({
      mutationFn: async ({ id, data }) => {
        const response = await API.put(`${companyResourceUri}/${id}/zip-codes-serviced`, data);
        return response.data;
      },
      onSuccess: (data) => {
        ourQueryClient.setQueryData(getByIdQueryKey({ id: data.id }), data);
      },
      onError: (error) => reportException(error),
      ...mutationConfig
    });

    return { ...context, put: context.mutateAsync };
  };

  return {
    useList,
    list,
    useGetById,
    getById,
    create,
    useCreate,
    getCompanyByDnis: async (companyDNIS) => {
      const callCenterId = CallCenterStorage.callCenterId();
      const response = await unpagedList<Company, CompanyQueryParams>({
        params: { serializer: 'full', companyDNIS, ...(callCenterId ? { callCenterId } : {}) }
      });

      // List will be returned. Should contain 0..1.
      return response.results[0] || null;
    },
    listQueryKey,
    getByIdQueryKey,
    unpagedList,
    useUnpagedList,
    unpagedListQueryKey,
    unpagedListFull: unpagedList,
    useUnpagedListFull: useUnpagedList,
    put,
    usePut,
    deleteItem,
    useDelete,
    reactivate,
    useReactivate,
    useUpdateServicedZipCodes
  };
};
